import { AlertCircle, ArrowUpRight, CheckCircle2, Clock3 } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import BookDownloadButtons from "@/components/BookDownloadButtons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult() {
  const [params] = useSearchParams();
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const stripeSessionId = params.get("session_id");
  const kiwifyOrderId = params.get("kiwify_order_id") || params.get("transaction_id");
  const orderId = params.get("external_reference") || params.get("order_id") || kiwifyOrderId;
  const paymentId = params.get("payment_id");
  const resultType = params.get("result");
  const isStripe = Boolean(stripeSessionId);
  const isKiwify = resultType === "kiwify" || params.get("provider") === "kiwify" || Boolean(kiwifyOrderId);

  useEffect(() => {
    if (resultType === "cancel") {
      setError("Pagamento cancelado. Nenhuma cobrança foi confirmada.");
      return undefined;
    }
    if (!orderId && !stripeSessionId) {
      setError("Não encontramos o número deste pedido.");
      return undefined;
    }
    let active = true;
    let timer;
    const checkStatus = async (attempt = 0) => {
      try {
        let data;
        if (isKiwify) {
          const response = await fetch(`${API}/payments/kiwify/${encodeURIComponent(orderId)}`);
          data = await response.json();
          if (!response.ok) throw new Error(data.detail || "Não foi possível consultar a compra Kiwify.");
        } else if (isStripe) {
          const stripeResponse = await fetch(`${API}/payments/status/${encodeURIComponent(stripeSessionId)}`);
          data = await stripeResponse.json();
          if (!stripeResponse.ok) throw new Error(data.detail || "Não foi possível consultar o pagamento Stripe.");
          if (data.payment_status === "paid" && orderId) {
            const orderResponse = await fetch(`${API}/orders/${encodeURIComponent(orderId)}`);
            const orderData = await orderResponse.json();
            if (orderResponse.ok) data = { ...data, ...orderData };
          }
        } else {
          const query = paymentId ? `?payment_id=${encodeURIComponent(paymentId)}` : "";
          const response = await fetch(`${API}/orders/${encodeURIComponent(orderId)}${query}`);
          data = await response.json();
          if (!response.ok) throw new Error(data.detail || "Não foi possível consultar o pedido.");
        }
        if (!active) return;
        setResult(data);
        if (!(data.status === "approved" && data.fulfilled) && attempt < 9) timer = window.setTimeout(() => checkStatus(attempt + 1), 3000);
      } catch (requestError) {
        if (active) setError(requestError.message);
      }
    };
    checkStatus();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [isKiwify, isStripe, orderId, paymentId, resultType, stripeSessionId]);

  const contentAccess = () => {
    if (!result?.content_url) return null;
    const contentUrl = new URL(result.content_url, API);
    const pathParts = contentUrl.pathname.split("/").filter(Boolean);
    return {
      orderId: pathParts[pathParts.length - 1],
      token: contentUrl.searchParams.get("token") || "",
    };
  };

  const readerHref = () => {
    const access = contentAccess();
    if (!access?.orderId || !access.token) return "/";
    return `/leitura?order=${encodeURIComponent(access.orderId)}&token=${encodeURIComponent(access.token)}`;
  };

  const providerName = isKiwify ? "a Kiwify" : isStripe ? "o Stripe" : "o Mercado Pago";
  const approved = result?.status === "approved" && result?.fulfilled;
  const access = contentAccess();
  return (
    <main className="payment-result-page" data-testid="payment-result-container">
      <div className="payment-result-box">
        <span className="reader-brand">30 DIAS <span>PARA A LIBERDADE</span></span>
        {error && <><AlertCircle size={28} /><h1>Pagamento não confirmado.</h1><p data-testid="payment-result-error">{error}</p><a className="button button-secondary" href="/">Voltar ao início</a></>}
        {!error && !result && <><Clock3 size={28} /><h1>Confirmando automaticamente…</h1><p data-testid="payment-result-loading">Aguarde enquanto confirmamos o pagamento com {providerName}.</p></>}
        {approved && <><CheckCircle2 size={30} /><h1>Pagamento confirmado.</h1><p data-testid="payment-approved-message">Seu acesso ao livro foi liberado automaticamente.</p><a className="button button-primary" href={readerHref()} data-testid="open-protected-book-button">Abrir o livro <ArrowUpRight size={17} /></a>{access && <BookDownloadButtons endpoint={`/content/${encodeURIComponent(access?.orderId || "")}/download`} query={access?.token ? `?token=${encodeURIComponent(access.token)}` : ""} testIdPrefix="payment" />}</>}
        {result && !approved && <><Clock3 size={28} /><h1>Pagamento em confirmação.</h1><p data-testid="payment-pending-message">O livro será liberado somente após confirmação de pagamento. Não é necessária aprovação manual.</p><a className="button button-secondary" href="/">Voltar ao início</a></>}
      </div>
    </main>
  );
}
